/**
 * MarketingPerformanceCockpit
 *
 * Consolidated Lead Performance surface.
 *
 * DEFAULT (merged) layout — 4 tabs:
 *   • Lead Sources   — unified Channels + Sources + Integrity (sub-pivot inside)
 *   • Attribution    — lead → revenue path
 *   • Campaigns      — campaign ROI · pacing
 *   • Call Center    — unified Calls + Dispositions (sub-pivot inside)
 *
 * LEGACY layout (7 tabs) — restored by appending `?legacy=1` to the URL.
 * This is a one-flag rollback. No routes, components, or engines change;
 * only the cockpit's tab assembly is different. Every feature in the merged
 * "Lead Sources" and "Call Center" tabs is reachable through their sub-pivots.
 */
import { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import GrowthChannelsCockpit from "./GrowthChannelsCockpit";
import SourcesLens from "./marketing/SourcesLens";
import GrowthMarketingAttributionServer from "./marketing/GrowthMarketingAttributionServer";
import GrowthDispositionServer from "./marketing/GrowthDispositionServer";
import LeadIntegrityRoute from "@/pages/LeadIntegrityRoute";
import { cn } from "@/lib/utils";
import { Layers, ListFilter, FileText, BarChart3, Shield, Phone, Megaphone, Radar } from "lucide-react";
import { CockpitTabs } from "@/components/cockpit/CockpitShell";
import { CockpitPage } from "@/components/cockpit/CockpitPage";
import { SubPivot } from "@/components/cockpit/SubPivot";
// RangeToggle intentionally not imported — see note in component body.
// import { RangeToggle, useRange } from "@/components/cockpit/RangeToggle";

/* ─── Tabs ─────────────────────────────────────────────────────────── */

// Merged top-level tab IDs (default layout).
type MergedTab = "lead-sources" | "attribution" | "campaigns" | "callcenter";

// Legacy 7-tab IDs (restored via ?legacy=1).
type LegacyTab = "channels" | "sources" | "integrity" | "attribution" | "campaigns" | "callcenter" | "dispositions";

// Sub-pivot inside the merged "Lead Sources" tab.
type LeadSourcesPivot = "channels" | "sources" | "integrity";

// Sub-pivot inside the merged "Call Center" tab.
type CallCenterPivot = "calls" | "dispositions";

const MERGED_TABS: readonly MergedTab[] = ["lead-sources", "attribution", "campaigns", "callcenter"];
const LEGACY_TABS: readonly LegacyTab[] = ["channels", "sources", "integrity", "attribution", "campaigns", "callcenter", "dispositions"];
const PIVOTS:      readonly LeadSourcesPivot[] = ["channels", "sources", "integrity"];
const CC_PIVOTS:   readonly CallCenterPivot[] = ["calls", "dispositions"];

function isPivot(v: string | null): v is LeadSourcesPivot {
  return !!v && (PIVOTS as readonly string[]).includes(v);
}
function isCcPivot(v: string | null): v is CallCenterPivot {
  return !!v && (CC_PIVOTS as readonly string[]).includes(v);
}

/* ─── Component ────────────────────────────────────────────────────── */

export default function MarketingPerformanceCockpit({
  lensControls,
}: { lensControls?: React.ReactNode } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const legacy = searchParams.get("legacy") === "1";
  const urlTab = searchParams.get("tab");
  const urlPivot = searchParams.get("pivot");
  const urlChannel = searchParams.get("channel");

  /* ─ Tab resolution ───────────────────────────────────────────────── */

  // Map any incoming tab id to the right slot in the active layout.
  const initialTab = useMemo<MergedTab | LegacyTab>(() => {
    if (legacy) {
      if (urlTab && (LEGACY_TABS as readonly string[]).includes(urlTab)) return urlTab as LegacyTab;
      return location.pathname === "/lead-sources" ? "sources" : "channels";
    }
    // Merged layout: legacy ids fold into the right merged tab.
    if (urlTab === "channels" || urlTab === "sources" || urlTab === "integrity") return "lead-sources";
    if (urlTab === "dispositions") return "callcenter";
    if (urlTab && (MERGED_TABS as readonly string[]).includes(urlTab)) return urlTab as MergedTab;
    // Default landing: Attribution — the "did leads → revenue?" answer leadership opens this page for.
    // Tab order in the UI stays chronological (Lead Sources → Attribution → Campaigns → Call Center).
    return "attribution";
  }, [legacy, urlTab, location.pathname]);

  const [tab, setTab] = useState<MergedTab | LegacyTab>(initialTab);
  // Keep state in sync when the URL changes via in-app navigation (e.g. sidebar
  // links or back/forward) without remounting the component.
  useEffect(() => { setTab(initialTab); }, [initialTab]);

  // Pivot inside the merged "Lead Sources" tab. Honors legacy tab ids in the URL.
  const initialPivot = useMemo<LeadSourcesPivot>(() => {
    if (isPivot(urlPivot)) return urlPivot;
    if (urlTab === "sources" || urlTab === "integrity") return urlTab;
    if (location.pathname === "/lead-sources") return "sources";
    return "channels";
  }, [urlPivot, urlTab, location.pathname]);

  const [pivot, setPivot] = useState<LeadSourcesPivot>(initialPivot);
  useEffect(() => { setPivot(initialPivot); }, [initialPivot]);

  // Pivot inside the merged "Call Center" tab. Honors legacy `tab=dispositions` deep links.
  const initialCcPivot = useMemo<CallCenterPivot>(() => {
    if (isCcPivot(urlPivot)) return urlPivot;
    if (urlTab === "dispositions") return "dispositions";
    return "calls";
  }, [urlPivot, urlTab]);

  const [ccPivot, setCcPivot] = useState<CallCenterPivot>(initialCcPivot);
  useEffect(() => { setCcPivot(initialCcPivot); }, [initialCcPivot]);

  // Disclosure-reveal nonce. Bumps ONLY on parent tab change so the cascade plays
  // when the sub-pivot first appears. Pivot-to-pivot clicks within the same tab
  // do NOT re-key — that caused a flicker as every chip re-ran the entrance
  // animation on each click. The selected-state color transition is enough
  // feedback for in-tab toggling.
  const [revealNonce, setRevealNonce] = useState(0);
  useEffect(() => { setRevealNonce(n => n + 1); }, [tab]);

  /* ─ URL sync (no remount) ────────────────────────────────────────── */

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (next.get("tab") !== tab) next.set("tab", tab);

    if (!legacy && tab === "lead-sources") {
      if (next.get("pivot") !== pivot) next.set("pivot", pivot);
    } else if (!legacy && tab === "callcenter") {
      if (next.get("pivot") !== ccPivot) next.set("pivot", ccPivot);
    } else {
      next.delete("pivot");
    }

    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [tab, pivot, ccPivot, legacy, searchParams, setSearchParams]);

  // Normalize /lead-sources → /growth-channels?tab=...
  useEffect(() => {
    if (location.pathname === "/lead-sources") {
      const target = legacy
        ? `/growth-channels?tab=${tab}`
        : `/growth-channels?tab=lead-sources&pivot=${pivot}`;
      navigate(target, { replace: true });
    }
  }, [location.pathname, tab, pivot, legacy, navigate]);

  /* ─ Render ───────────────────────────────────────────────────────── */

  // Merged-mode tab definitions (label + sub-label) used by both the new cockpit strip and the classic pills.
  const mergedTabDefs = [
    { value: "lead-sources" as const, label: "Lead Sources", sub: "channels · sources · integrity", icon: <Radar     className="h-3.5 w-3.5" /> },
    { value: "attribution"  as const, label: "Attribution",  sub: "lead → revenue path",            icon: <BarChart3 className="h-3.5 w-3.5" /> },
    { value: "campaigns"    as const, label: "Campaigns",    sub: "campaign ROI · pacing",          icon: <Megaphone className="h-3.5 w-3.5" /> },
    { value: "callcenter"   as const, label: "Call Center",  sub: "calls · dispositions",           icon: <Phone     className="h-3.5 w-3.5" /> },
  ];
  const activeMergedSub = mergedTabDefs.find(t => t.value === tab)?.sub;

  const showCockpit = !legacy;       // merged view → hairline tabs only
  const showClassic = legacy;        // ?legacy=1 retains the original 7-pill layout

  // Pill strip — only rendered in legacy mode now. Kept here so /growth-channels?legacy=1 still works.
  const pillStrip = showClassic ? (
    <div role="tablist" aria-label="Marketing lens" className="inline-flex items-center rounded-lg border border-border/60 bg-muted/30 p-0.5 flex-wrap">
      <LensTab active={tab === "channels"}     onClick={() => setTab("channels")}     icon={<Layers className="h-3.5 w-3.5" />}     label="Channels"    sub="Net CPA · spend · ROI" />
      <LensTab active={tab === "sources"}      onClick={() => setTab("sources")}      icon={<ListFilter className="h-3.5 w-3.5" />} label="Sources"     sub="quality · dupes · conv" />
      <LensTab active={tab === "integrity"}    onClick={() => setTab("integrity")}    icon={<Shield className="h-3.5 w-3.5" />}     label="Integrity"   sub="intake quality" />
      <LensTab active={tab === "attribution"}  onClick={() => setTab("attribution")}  icon={<BarChart3 className="h-3.5 w-3.5" />}  label="Attribution" sub="lead → revenue path" />
      <LensTab active={tab === "campaigns"}    onClick={() => setTab("campaigns")}    icon={<Megaphone className="h-3.5 w-3.5" />}  label="Campaigns"   sub="campaign ROI · pacing" />
      <LensTab active={tab === "callcenter"}   onClick={() => setTab("callcenter")}   icon={<Phone className="h-3.5 w-3.5" />}      label="Call Center" sub="speed · show · close" />
      <LensTab active={tab === "dispositions"} onClick={() => setTab("dispositions")} icon={<FileText className="h-3.5 w-3.5" />}   label="Dispositions" sub="appt outcomes" />
    </div>
  ) : null;

  // NOTE: Page-level Week/Month/Quarter range toggle was removed because
  // downstream KPIs/tables/charts don't yet filter by it — it was visual-only
  // and misleading. Re-mount <RangeToggle/> in `controls` once the data layer
  // honors `useRange()`. The hook + URL contract (?range=) stays intact.

  // Legacy mode keeps its 7-pill bar inline; merged mode uses the standard.
  if (legacy) {
    return (
      <div className="max-w-6xl mx-auto pb-12 pt-2">
        <header className="mb-3">
          <div className="flex items-start justify-between gap-4 flex-wrap pb-4 border-b border-border">
            <div>
              <h1 className="kdr-display text-foreground">Leads</h1>
              <p className="kdr-meta mt-1.5 max-w-xl">
                Legacy layout — 7 tabs. Remove ?legacy=1 to return to the consolidated view.
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {pillStrip}
            </div>
          </div>
        </header>
        {renderTabContent()}
      </div>
    );
  }

  function renderTabContent() {
    return (
      <>
        {/* ─── MERGED: Lead Sources tab with sub-pivot ─────────────────── */}
        {!legacy && tab === "lead-sources" && (
          <>
            <SubPivot<LeadSourcesPivot>
              value={pivot}
              onChange={setPivot}
              revealKey={`ls-${revealNonce}`}
              options={[
                { id: "channels",  label: "Channels",  sub: "spend · CPA · ROI",        icon: <Layers     className="h-3.5 w-3.5" /> },
                { id: "sources",   label: "Sources",   sub: "quality · dupes · conv",   icon: <ListFilter className="h-3.5 w-3.5" /> },
                { id: "integrity", label: "Integrity", sub: "intake records · scrubs",  icon: <Shield     className="h-3.5 w-3.5" /> },
              ]}
            />
            {pivot === "channels"  && <GrowthChannelsCockpit embedded />}
            {pivot === "sources"   && (
              <SourcesLens
                defaultChannel={urlChannel ?? undefined}
              />
            )}
            {pivot === "integrity" && <div data-embedded-report="lead-performance"><LeadIntegrityRoute embedded /></div>}
          </>
        )}

        {/* ─── MERGED: Call Center tab with sub-pivot ──────────────────── */}
        {!legacy && tab === "callcenter" && (
          <>
            <SubPivot<CallCenterPivot>
              value={ccPivot}
              onChange={setCcPivot}
              revealKey={`cc-${revealNonce}`}
              options={[
                { id: "calls",        label: "Calls",        sub: "speed · show · close", icon: <Phone    className="h-3.5 w-3.5" /> },
                { id: "dispositions", label: "Dispositions", sub: "appt outcomes",        icon: <FileText className="h-3.5 w-3.5" /> },
              ]}
            />
            {ccPivot === "calls"        && <div data-embedded-report="lead-performance"><GrowthMarketingAttributionServer section="callcenter" /></div>}
            {ccPivot === "dispositions" && <div data-embedded-report="lead-performance"><GrowthDispositionServer /></div>}
          </>
        )}

        {/* ─── LEGACY: original 7 standalone tabs ──────────────────────── */}
        {legacy && tab === "channels" && <GrowthChannelsCockpit embedded />}
        {legacy && tab === "sources" && (
          <SourcesLens
            defaultChannel={urlChannel ?? undefined}
          />
        )}
        {legacy && tab === "integrity"    && <div data-embedded-report="lead-performance"><LeadIntegrityRoute embedded /></div>}
        {legacy && tab === "callcenter"   && <div data-embedded-report="lead-performance"><GrowthMarketingAttributionServer section="callcenter" /></div>}
        {legacy && tab === "dispositions" && <div data-embedded-report="lead-performance"><GrowthDispositionServer /></div>}

        {/* ─── Shared across both layouts ──────────────────────────────── */}
        {tab === "attribution"  && <div data-embedded-report="lead-performance"><GrowthMarketingAttributionServer section="attribution" /></div>}
        {tab === "campaigns"    && <div data-embedded-report="lead-performance"><GrowthMarketingAttributionServer section="campaigns" /></div>}
      </>
    );
  }

  const PIVOT_LABEL: Record<LeadSourcesPivot, string> = {
    channels: "Channels",
    sources: "Sources",
    integrity: "Integrity",
  };
  const CC_PIVOT_LABEL: Record<CallCenterPivot, string> = {
    calls: "Calls",
    dispositions: "Dispositions",
  };
  const tabLabel = mergedTabDefs.find(t => t.value === tab)?.label
    ?? (tab === "channels" ? "Channels"
      : tab === "sources" ? "Sources"
      : tab === "integrity" ? "Integrity"
      : tab === "dispositions" ? "Dispositions"
      : undefined);
  const titleSuffix = !legacy && tab === "lead-sources"
    ? PIVOT_LABEL[pivot]
    : !legacy && tab === "callcenter"
      ? CC_PIVOT_LABEL[ccPivot]
      : tabLabel;

  return (
    <CockpitPage
      title="Leads"
      titleSuffix={titleSuffix}
      controls={lensControls}
      tabs={
        <CockpitTabs
          tabs={mergedTabDefs.map(t => ({ value: t.value, label: t.label }))}
          value={tab}
          onChange={(v) => setTab(v as MergedTab)}
        />
      }
    >
      {renderTabContent()}
    </CockpitPage>
  );
}

/* ─── Primitives ─────────────────────────────────────────────────────── */

function LensTab({ active, onClick, icon, label, sub }: {
  active: boolean; onClick: () => void; icon: React.ReactNode; label: string; sub: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "flex items-center gap-2 px-3 py-1.5 rounded-md text-left transition-all duration-200",
        active
          ? "bg-primary text-primary-foreground shadow-md ring-1 ring-primary/40"
          : "text-muted-foreground hover:text-foreground hover:bg-background/60",
      )}
    >
      {icon}
      <span className="flex flex-col leading-tight">
        <span className="text-[12px] font-semibold">{label}</span>
        <span className={cn("text-[10px]", active ? "text-primary-foreground/90" : "text-muted-foreground/70")}>{sub}</span>
      </span>
    </button>
  );
}
